import React, { useState } from 'react';
import styled from 'styled-components';
import { panel } from './panelTheme';
import { SearchInput } from 'src/components/widgets/input';
import { ChevronIcon, PlusIcon } from 'src/components/widgets/icons/scene';
import { useDropdown } from 'src/components/widgets/hooks/useDropdown';
import { thinScrollbar } from 'src/components/widgets/scrollbar';
import { T } from 'src/components/widgets/helpers/helpers';

// L'ELENCO DA CUI SI SCEGLIE UN ASSET — un materiale, una texture, un ambiente.
//
// Era una tendina di nomi, ed è il minimo che si possa mostrare di una libreria: fra «Inner.007» e
// «Other.006» non sceglie nessuno leggendo. Un asset si riconosce dalla sua faccia, si cerca per
// nome quando le facce sono cento, e ogni tanto quello che serve non c'è ancora — e allora si fa.
// Tre cose, e stanno tutte e tre qui dentro:
//
//     IN TESTA il comando che ne crea uno nuovo, dove ci si accorge che manca;
//     SOTTO la ricerca — la stessa del backoffice, non una copia più piccola;
//     POI le voci, con l'anteprima e il nome, e quella corrente accesa.
//
// STA NEL DESIGN SYSTEM E NON NELLO SLOT perché i posti da cui si sceglie un asset sono già tre —
// il materiale di una mesh, la texture di un canale, l'ambiente del World — e la seconda volta che
// una cosa serve, il posto è qui.
//
// E NON SERVE SOLO A SCEGLIERE. La stessa forma — righe con la propria marca, il nome, e quella
// corrente accesa — è anche la risposta a «dove sei usato»: gli oggetti a cui un materiale è
// assegnato si aprono dalla pastiglia della testata e atterrano qui dentro. Cambia che cosa
// succede scegliendo una riga — si assegna, oppure ci si va — non com'è fatto l'elenco.

/**
 * L'ANTEPRIMA DI UN ASSET, TONDA. È la fotografia di una sfera illuminata: tonda perché è quella
 * la forma di ciò che ritrae, e perché in un elenco distingue un materiale da una texture — che è
 * un'immagine, e sta in un quadrato.
 *
 * Senza fotografia resta il cerchio vuoto: dice «questo asset esiste, la sua faccia non è ancora
 * stata scattata», dove un buco direbbe che manca la riga.
 */
// LA SUPERFICIE DELLA SFERA, in tre casi e un posto solo.
//
//     LA FOTOGRAFIA quando c'è: è più precisa di qualunque categoria;
//     LA TINTA DELLA SPECIE quando non c'è: una pallina rosa dice «materiale» prima che si legga
//         il nome, dove un cerchio grigio direbbe soltanto «qui manca qualcosa»;
//     NIENTE quando non c'è nemmeno quella: resta il solo anello, che è il disegno di un posto.
const swatchSurface = (props: { $url?: string; $tint?: string }) => {
    if (props.$url) return `url(${props.$url})`;
    if (!props.$tint) return 'none';

    // IL RIFLESSO È LA RICETTA DEL PROTOTIPO, copiata: la luce in alto a sinistra, la tinta al 62%,
    // l'ombra sul bordo. Non è decorazione — è ciò che fa leggere il cerchio come una SUPERFICIE
    // invece che come un bollino, ed è così che un materiale si riconosce a diciotto pixel.
    return `radial-gradient(circle at 33% 28%, rgba(255, 255, 255, 0.85), rgba(255, 255, 255, 0.12) 38%, ${props.$tint} 62%, rgba(0, 0, 0, 0.35))`;
};

export const PropertiesPanelSwatch = styled.span<{ $url?: string; $size?: number; $tint?: string }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: ${(props) => props.$size ?? 20}px;
    height: ${(props) => props.$size ?? 20}px;
    aspect-ratio: 1 / 1;
    border-radius: 50%;

    /* L'ANELLO È INTERNO e non un bordo: un bordo aggiungerebbe due pixel alla misura chiesta, e
       chi la chiede la usa per allinearsi ad altro. Il tono viene dal testo e non da una riga di
       colore scritta a mano, così vale anche a tema scuro — dove un grigio fisso sparirebbe. */
    box-shadow: inset 0 0 0 1px color-mix(in srgb, ${panel.ink} 16%, transparent);
    background-color: transparent;
    background-image: ${swatchSurface};
    background-size: cover;
    background-position: center;
    color: ${panel.inkMuted};
    overflow: hidden;

    svg {
        width: 12px;
        height: 12px;
    }
`;

const Picker = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 244px;
    max-width: 100%;
    padding: 6px;
`;

// IL COMANDO CHE CREA STA IN TESTA E PORTA L'ACCENTO. È l'unica voce di questo elenco che non
// sceglie: aggiunge. Il colore la separa dalle altre meglio di una riga di divisione, che in un
// pannello alto duecento pixel sarebbe la terza linea orizzontale in vista.
const Create = styled.button`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 6px 8px;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: ${panel.accent};
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;

    &:hover,
    &:focus-visible {
        outline: none;
        background: color-mix(in srgb, ${panel.accent} 10%, transparent);
    }
`;

const CreateGlyph = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    background: color-mix(in srgb, ${panel.accent} 14%, transparent);

    svg {
        width: 12px;
        height: 12px;
    }
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1px;
    max-height: 240px;
    overflow-y: auto;
    ${thinScrollbar}
`;

const Item = styled.button<{ $current?: boolean }>`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 5px 8px;
    border: 0;
    border-radius: 6px;
    background: ${(props) => (props.$current ? `color-mix(in srgb, ${panel.accent} 12%, transparent)` : 'transparent')};

    /* QUELLA DI ADESSO SI LEGGE ARANCIO, e non solo dal fondo acceso: in un elenco di quaranta
       nomi il fondo lo si vede quando ci si è già sopra, il colore della parola si trova
       scorrendo. */
    color: ${(props) => (props.$current ? panel.accent : panel.ink)};
    font-family: inherit;
    font-size: 12px;
    font-weight: ${(props) => (props.$current ? 500 : 400)};
    text-align: left;
    cursor: pointer;

    &:hover,
    &:focus-visible {
        outline: none;
        background: ${(props) => (props.$current ? `color-mix(in srgb, ${panel.accent} 12%, transparent)` : panel.field)};
    }
`;

// IL NOME E CIÒ CHE IL NOME NON DICE. Fra due mesh chiamate «Zone 1» e «Zone 2» il nome non
// distingue niente: il conto dei triangoli dice quale delle due è il pezzo grande. Sta sotto ed è
// più piccolo, perché è la seconda cosa che si legge — e quando non c'è, la riga resta di un rigo.
const Text = styled.span`
    display: flex;
    flex-direction: column;
    min-width: 0;
`;

const Label = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const Caption = styled.span`
    min-width: 0;
    color: ${panel.inkMuted};
    font-size: 10px;
    font-weight: 400;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// LA MARCA DI CHI UNA FACCIA NON CE L'HA. La pallina ritrae una superficie, ed è giusta per un
// materiale o una texture; un oggetto una superficie non è, e la sua marca è il glifo della sua
// specie — lo stesso che porta nella riga del suo albero.
const Mark = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 0 0 auto;
    width: 20px;
    height: 20px;
    border-radius: 6px;
    background: ${panel.field};
    color: ${panel.inkMuted};

    svg {
        width: 12px;
        height: 12px;
    }

    /* Le icone Material si riempiono; i glifi della scena restano a contorno. */
    svg:not([stroke]) {
        fill: currentColor;
    }
`;

// CERCARE E NON TROVARE NIENTE È UN RISULTATO, non un errore: l'elenco che si svuota senza dire
// niente sembra rotto.
const Empty = styled.p`
    margin: 0;
    padding: 10px 8px;
    color: ${panel.inkMuted};
    font-size: 12px;
    text-align: center;
`;

export type PropertiesPanelPickerOption = {
    value: string | number;
    /** Il nome che si legge, e su cui si cerca. */
    label: string;
    /** Ciò che il nome non dice: «26.880 triangles». */
    caption?: string;
    /** La fotografia di quell'asset, quando è già stata scattata. */
    previewUrl?: string;
    /** Al posto della pallina: il glifo della specie, per chi una faccia non ce l'ha. */
    mark?: React.ReactNode;
};

export type PropertiesPanelPickerProps = {
    options: PropertiesPanelPickerOption[];
    /** Quale è quella di adesso: si accende, e non serve cercarla. */
    current?: string | number;
    onSelect: (value: string | number) => void;
    /** Il comando in testa che ne crea uno nuovo. Senza, la riga non c'è. */
    create?: { label: React.ReactNode; onClick: () => void };
    searchPlaceholder?: string;
    emptyLabel?: React.ReactNode;
    /** La tinta della specie, per le voci che una fotografia non ce l'hanno. */
    tint?: string;
    /** Un elenco che è già una risposta non si cerca: vedi il campo, più sotto. */
    search?: boolean;
    /** Voci di sola parola, senza pallina né glifo: una scelta che non ha una faccia da mostrare. */
    bare?: boolean;
};

export const PropertiesPanelPicker: React.FC<PropertiesPanelPickerProps> = ({
    options,
    current,
    onSelect,
    create,
    searchPlaceholder,
    emptyLabel,
    tint,
    search = true,
    bare
}) => {
    const [query, setQuery] = useState('');

    const needle = query.trim().toLowerCase();
    const visible = needle ? options.filter((option) => option.label.toLowerCase().includes(needle)) : options;

    return (
        <Picker>
            {create && (
                <Create type='button' onClick={create.onClick}>
                    <CreateGlyph aria-hidden='true'>
                        <PlusIcon />
                    </CreateGlyph>
                    {create.label}
                </Create>
            )}

            {/* IL CAMPO NON DIPENDE DAL NUMERO DI VOCI. Un campo che compare oltre una soglia è
                un'interfaccia che cambia forma da sola: chi ha imparato a cercare qui lo cerca anche
                nella libreria piccola, e non lo trova. A deciderlo è il MESTIERE dell'elenco, e lo
                dichiara chi lo monta: si cerca dove si SCEGLIE fra i tanti di una libreria, non dove
                l'elenco è già la risposta a una domanda fatta — «a quali oggetti è assegnato questo
                materiale», che sono quelli e si contano. */}
            {search && (
                <SearchInput
                    autoFocus
                    value={query}
                    placeholder={searchPlaceholder ?? T._('Search...', 'Admin')}
                    aria-label={searchPlaceholder ?? T._('Search...', 'Admin')}
                    onChange={(event) => setQuery(event.currentTarget.value)}
                />
            )}

            <List>
                {visible.map((option, index) => (
                    /* IL FUOCO DEVE ENTRARE QUI DENTRO. Con la ricerca ci entra da solo — lo prende il
                       campo — ma dove il campo non c'è resterebbe sulla maniglia, e l'elenco vive in fondo
                       al documento: da tastiera ci si arriverebbe attraversando tutto il resto. */
                    <Item
                        key={option.value}
                        type='button'
                        autoFocus={!search && index === 0}
                        $current={option.value === current}
                        onClick={() => onSelect(option.value)}
                    >
                        {!bare &&
                            (option.mark ? (
                                <Mark aria-hidden='true'>{option.mark}</Mark>
                            ) : (
                                <PropertiesPanelSwatch $url={option.previewUrl} $tint={tint} aria-hidden='true' />
                            ))}
                        <Text>
                            <Label>{option.label}</Label>
                            {option.caption && <Caption>{option.caption}</Caption>}
                        </Text>
                    </Item>
                ))}

                {visible.length === 0 && <Empty>{emptyLabel ?? T._('Nothing found', 'Admin')}</Empty>}
            </List>
        </Picker>
    );
};

// IL MENU DEL PANNELLO — una maniglia grande quanto un campo, e sotto lo stesso elenco da cui si
// sceglie un asset. Il menu di react-select si apriva col suo disegno — l'azzurro della voce
// accesa, un'ombra sua — e accanto ai campi della scena sembrava arrivato da un'altra
// applicazione. Nel mockup il menu è la maniglia più l'elenco degli asset, e qui è lo stesso.
const FieldTrigger = styled.button`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    width: 100%;
    min-width: 0;
    height: 26px;
    padding: 0 6px 0 8px;
    border: 1px solid ${panel.line};
    border-radius: 5px;
    background: ${panel.field};
    color: ${panel.ink};
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    text-align: left;
    cursor: pointer;

    &:focus-visible,
    &[aria-expanded='true'] {
        outline: none;
        border-color: ${panel.accent};
    }

    /* Spento perde la superficie, come un campo: il fondo incassato è ciò che dice «qui si sceglie». */
    &:disabled {
        background: transparent;
        cursor: not-allowed;
    }

    /* Il chevron nasce rivolto a destra: chiuso guarda in giù, dove si apre; aperto guarda in su. */
    svg {
        flex: none;
        width: 8px;
        height: 8px;
        color: ${panel.inkMuted};
        transform: rotate(90deg);
        transition: transform 130ms ease;
    }

    &[aria-expanded='true'] svg {
        transform: rotate(-90deg);
    }
`;

const FieldValue = styled.span`
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

export type PropertiesPanelPickerFieldProps = {
    id?: string;
    options: PropertiesPanelPickerOption[];
    value?: string | number;
    onChange: (value: string | number) => void;
    /** Il nome della scelta, per chi non vede la riga: si legge insieme al valore. */
    ariaLabel?: string;
    disabled?: boolean;
};

// LE VOCI SONO SOLO PAROLE: qui si sceglie un valore, non un asset, e una pallina vuota accanto a
// «UV0» direbbe che gli manca una faccia. L'elenco è largo quanto la maniglia, così si apre sotto
// il campo e non oltre la colonna.
export const PropertiesPanelPickerField: React.FC<PropertiesPanelPickerFieldProps> = ({ id, options, value, onChange, ariaLabel, disabled }) => {
    const [openDropdown, closeDropdown, isOpen, Dropdown] = useDropdown();
    const current = options.find((option) => option.value === value);

    return (
        <>
            <FieldTrigger
                id={id}
                type='button'
                aria-expanded={isOpen}
                aria-label={ariaLabel ? `${ariaLabel}, ${current?.label ?? ''}` : undefined}
                disabled={disabled}
                onClick={(event) => (isOpen ? closeDropdown() : openDropdown(event.currentTarget, 'bottom-start'))}
            >
                <FieldValue>{current?.label}</FieldValue>
                <ChevronIcon aria-hidden='true' />
            </FieldTrigger>
            {isOpen && (
                <Dropdown matchTriggerWidth>
                    <PropertiesPanelPicker
                        search={false}
                        bare
                        options={options}
                        current={value}
                        onSelect={(next) => {
                            closeDropdown();
                            onChange(next);
                        }}
                    />
                </Dropdown>
            )}
        </>
    );
};
